import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { FAQ } from "../../data/landing";
import { FEATURED_GUIDES } from "../../data/guidesIndex";

export const FounderSection = () => (
  <section id="founder" className="border-y border-slate-200 bg-slate-900">
    <div className="mx-auto grid max-w-5xl items-center gap-10 px-4 py-16 sm:px-6 md:grid-cols-[260px,1fr] lg:py-24">
      <div className="mx-auto w-56">
        <img
          src="/scott-herberg.jpg"
          alt="Scott Herberg, founder of Fast Estimate"
          data-testid="founder-photo"
          className="aspect-square w-full rounded-2xl bg-slate-700 object-cover"
          onError={(e) => { e.currentTarget.style.visibility = "hidden"; e.currentTarget.parentElement.classList.add("rounded-2xl", "bg-slate-700", "aspect-square"); }}
        />
      </div>
      <div>
        <h2 className="font-heading text-3xl font-black text-white sm:text-4xl">Who built this</h2>
        <p data-testid="founder-copy" className="mt-5 text-lg leading-relaxed text-slate-300">
          Fast Estimate was built by Scott Herberg, who has spent 28 years in HVAC — most of it as a
          manufacturer's rep in the Pacific Northwest, working with the contractors this is built for.
          The estimate flow follows how a replacement job actually gets quoted in the driveway, not how
          software people think it works.
        </p>
        {/* TODO: add LinkedIn URL when supplied
        <a href="LINKEDIN_URL" target="_blank" rel="noopener noreferrer" className="mt-4 inline-block font-bold text-indigo-400">Connect with Scott on LinkedIn</a>
        */}
      </div>
    </div>
  </section>
);

export const FaqSection = () => (
  <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-24">
    <h2 className="text-center font-heading text-3xl font-black text-slate-900 sm:text-4xl">Questions, answered</h2>
    <Accordion type="single" collapsible className="mt-10 space-y-3">
      {FAQ.map((f, i) => (
        <AccordionItem key={f.q} value={`item-${i}`} data-testid={`faq-${i}`} className="rounded-xl border-2 border-slate-200 px-6">
          <AccordionTrigger className="py-5 text-left font-heading text-base font-bold text-slate-900 hover:no-underline">{f.q}</AccordionTrigger>
          <AccordionContent className="text-base text-slate-600">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const GuidesTeaser = () => (
  <section className="border-t border-slate-200 bg-slate-50">
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-wide text-indigo-500">From our guides</p>
          <h2 className="mt-2 font-heading text-3xl font-black text-slate-900 sm:text-4xl">HVAC cost &amp; estimating guides</h2>
        </div>
        <Link to="/guides" data-testid="guides-view-all" className="hidden shrink-0 items-center gap-1 text-sm font-bold text-indigo-600 hover:text-indigo-700 sm:flex">View all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {FEATURED_GUIDES.map((g) => (
          <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`home-guide-${g.slug}`} className="group flex flex-col rounded-2xl border-2 border-slate-200 bg-white p-6 transition-colors hover:border-indigo-400">
            <span className="text-xs font-bold uppercase tracking-wide text-indigo-500">{g.category}</span>
            <h3 className="mt-3 font-heading text-lg font-black text-slate-900 group-hover:text-indigo-600">{g.h1}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{g.metaDescription}</p>
            <span className="mt-4 flex items-center gap-1 text-sm font-bold text-indigo-500">Read <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>
        ))}
      </div>
      <Link to="/guides" className="mt-8 flex items-center justify-center gap-1 text-sm font-bold text-indigo-600 sm:hidden">View all guides <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

export const CtaBanner = () => (
  <section className="bg-indigo-500">
    <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6">
      <h2 className="font-heading text-3xl font-black text-white sm:text-4xl">Ready to quote faster?</h2>
      <p className="mt-3 text-lg text-indigo-100">Start your 7-day free trial today. No credit card required.</p>
      <Link to="/signup"><Button data-testid="cta-signup" className="mt-8 h-14 rounded-xl bg-white px-8 text-lg font-bold text-indigo-600 hover:bg-indigo-50">Start your free trial</Button></Link>
    </div>
  </section>
);
